"use strict";
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function") __typeError("Object expected");
    var dispose, inner;
    if (async) dispose = value[__knownSymbol("asyncDispose")];
    if (dispose === void 0) {
      dispose = value[__knownSymbol("dispose")];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") __typeError("Object not disposable");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  };
  var fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e);
  var next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0]) return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError) throw error;
  };
  return next();
};
/**
 * @license
 * This work is a derivative of the Google Neuroglancer project,
 * Copyright 2016 Google Inc.
 * The Derivative Work is covered by
 * Copyright 2019 Howard Hughes Medical Institute
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  AnonymousFirstCredentialsProvider,
  CredentialsProvider,
  makeCredentialsGetter
} from "#src/credentials_provider/index.js";
import { getCredentialsWithStatus } from "#src/credentials_provider/interactive_credentials_provider.js";
import { fetchOk } from "#src/util/http_request.js";
import { ProgressSpan } from "#src/util/progress_listener.js";
async function getAuthToken(authServer, signal) {
  const response = await fetchOk(authServer, {
    method: "GET",
    credentials: "include",
    signal
  });
  const token = await response.text();
  return { token };
}
class BaseDVIDCredentialsProvider extends CredentialsProvider {
  constructor(authServer) {
    super();
    this.authServer = authServer;
  }
  get = makeCredentialsGetter(async (options) => {
    var _stack = [];
    try {
      const { authServer } = this;
      if (!authServer) return { token: "" };
      const _span = __using(_stack, new ProgressSpan(options.progressListener, {
        message: `Requesting DVID access token from ${authServer}`
      }));
      return await getCredentialsWithStatus(
        {
          description: `DVID server ${this.authServer}`,
          supportsImmediate: true,
          get: async (signal, immediate) => {
            if (immediate) {
              return await getAuthToken(authServer, signal);
            }
            const match = authServer.match(/^[^/]+\/\/[^/.]+\.([^/]+)/);
            if (match) {
              const loginServer = `https://flyemlogin.${match[1]}/login`;
              throw new Error(
                `Please log into ${loginServer} and then refresh the neurogalncer page to try again.
If you are unable to log into ${loginServer}, please check your authorization server ${authServer} to make sure it is correct.`
              );
            } else {
              throw new Error(
                `Please check your authorization server ${authServer} to make sure it is correct.`
              );
            }
          }
        },
        options.signal
      );
    } catch (_) {
      var _error = _, _hasError = true;
    } finally {
      __callDispose(_stack, _error, _hasError);
    }
  });
}
export class DVIDCredentialsProvider extends AnonymousFirstCredentialsProvider {
  constructor(_dvidServer, authServer) {
    super(new BaseDVIDCredentialsProvider(authServer), {});
  }
}
